import React, { useState } from "react";

import { CircularIndeterminate } from "components";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";

import { RNHEstablecimientoUpd } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoUpd";

import { round6 } from "components/utils/mapa/useUbicacion";

import EstablecimientoFormulario from "pages/programa-salud/estructuras/establecimientos/EstablecimientoFormulario";

import CamposHospital, {
    ANCHOS_HOSPITAL,
    reglasHospital,
    useCatalogosHospital,
} from "./CamposHospital";

const TIPO = "H";

/**
 * Los valores con los que abre el formulario.
 *
 * Necesita los niveles ademas del registro: el establecimiento devuelve el nivel solo como Cod
 * ("Nivel III"), pero el select trabaja con ids y el Upd manda un id, asi que hay que
 * resolverlo contra el catalogo.
 *
 * La zona no viaja al servicio —se deduce del area— pero si es un campo del formulario, porque
 * es lo que filtra las areas que se pueden elegir.
 */
/**
 * Los valores del formulario, armados desde la FILA de la grilla.
 *
 * `niveles` sigue haciendo falta: el establecimiento guarda el nivel como COD y el select
 * trabaja con ids, asi que hay que resolverlo contra el catalogo. Es lo unico que no sale
 * directo de la fila.
 *
 * Se resuelve contra `row.nivelCod` y NO contra `row.nivel`, que es lo que ve la grilla: desde
 * que ahi se muestra el nombre, buscar por ese valor no encontraria ningun nivel y el select
 * abriria vacio.
 */
const valoresDelRegistro = (row, niveles) => ({
    id: row.id,
    version: row.version,
    cod: row.cod ?? "",
    name: row.name ?? "",
    nivelId: niveles.find((n) => n.rnhNivelCod === row.nivelCod)?.rnhNivelId ?? "",
    zonaId: row.zonaId ?? "",
    areaId: row.areaId ?? "",
    // El caracter que guarda el back, no el texto que muestra la grilla.
    cabecera: row.cabeceraCod ?? "",
    calle: row.calle ?? "",
    numero: row.numero ?? "",
    cpa: row.cpa ?? "",
    localidadId: row.localidadId ?? "",
    lat: row.lat ? round6(row.lat) : "",
    lng: row.lng ? round6(row.lng) : "",
});

export default function UpdateModal({ row, updateList, closeModal }) {
    const setSnackBarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);
    const [inRequest, setInRequest] = useState(false);

    // Se sigue esperando a los catalogos, pero ya no al registro: la fila lo trae entero.
    const { niveles, zonas, cargando } = useCatalogosHospital();

    const handleUpdateHospital = async (values) => {
        setInRequest(true);

        // id y version no los edita el formulario, se arrastran del detalle.
        const response = await RNHEstablecimientoUpd(credentials, {
            ...values,
            type: TIPO,
            id: row.id,
            version: row.version,
        });

        if (response.ok) {
            setSnackBarInfo({
                message: "Hospital modificado correctamente",
                severity: "success",
                open: true,
            });
            updateList();
            closeModal();
        }

        setInRequest(false);
    };

    // Se espera SOLO a los catalogos: sin los niveles, el nivel guardado no se puede
    // resolver a un id y el select abriria en blanco.
    if (cargando) return <CircularIndeterminate />;

    // Los valores se arman en el render y no en un state a proposito: Formik lee initialValues
    // una sola vez, al montarse, y el formulario no se monta hasta que hay datos. Un render
    // posterior arma un objeto nuevo con los mismos valores y Formik lo ignora.
    return (
        <EstablecimientoFormulario
            initialValues={valoresDelRegistro(row, niveles)}
            onSubmit={handleUpdateHospital}
            botonTexto="Guardar cambios"
            inRequest={inRequest}
            closeModal={closeModal}
            camposExtra={
                <CamposHospital
                    niveles={niveles}
                    zonas={zonas}
                    hospitalId={row.id}
                />
            }
            reglasExtra={reglasHospital}
            // Mitades y no 40/60: con diez campos la columna de carga ya le da al
            // mapa el alto que necesita, y angostarla obligaria a partir en dos filas
            // pares que hoy entran juntas.
            mapaAncho={false}
            columnas={ANCHOS_HOSPITAL}
        />
    );
}
